import { forwardRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Layout } from "@/components/Layout";
import { useCreateLogement } from "@/hooks/useLogements";
import { ApiError } from "@/api/client";

/**
 * Formulaire de création d'un logement (étape 1 du wizard de saisie).
 *
 * Couvre les champs administratifs uniquement. Le tracé du plan + parois +
 * systèmes sera dans le wizard plus avancé (chantier C6).
 */
const schema = z.object({
  title: z.string().min(1, "Donnez un nom à votre logement").max(100),
  type: z.enum(["maison", "appartement"]),
  postal_code: z
    .string()
    .regex(/^\d{5}$/, "Code postal à 5 chiffres")
    .optional()
    .or(z.literal("")),
  city: z.string().max(120).optional().or(z.literal("")),
  construction_year: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : Number(v)),
      z.number().int().min(1800).max(2100).optional(),
    ),
});

type FormData = z.infer<typeof schema>;

export default function LogementNewPage() {
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { type: "maison" },
  });

  const createMutation = useCreateLogement();

  const onSubmit = (data: FormData) => {
    setServerError(null);
    const payload = {
      title: data.title,
      type: data.type,
      postal_code: data.postal_code || undefined,
      city: data.city || undefined,
      construction_year: data.construction_year,
    };

    createMutation.mutate(payload, {
      onSuccess: (created) => navigate(`/logements/${created.id}`, { replace: true }),
      onError: (err: unknown) => {
        if (err instanceof ApiError && err.code === "LOGEMENT_LIMIT_REACHED") {
          setServerError(
            "Vous avez atteint la limite de logements pour votre offre. Pour ajouter d'autres logements, une offre payante sera proposée.",
          );
        } else {
          setServerError("Une erreur est survenue, veuillez réessayer.");
        }
      },
    });
  };

  const selectedType = watch("type");

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-6 py-12">
        <h1 className="text-2xl font-bold">Nouveau logement</h1>
        <p className="mt-1 text-sm text-gray-600">
          Décrivez votre logement. Vous pourrez compléter les détails techniques après.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-4" noValidate>
          <Field
            label="Nom du logement"
            placeholder="ex : Maison principale, Appart Paris 11"
            error={errors.title?.message}
            {...register("title")}
          />

          <fieldset>
            <legend className="mb-2 text-sm font-medium">Type</legend>
            <div className="flex gap-3">
              <TypeCard value="maison" label="Maison" current={selectedType} register={register} />
              <TypeCard
                value="appartement"
                label="Appartement"
                current={selectedType}
                register={register}
              />
            </div>
          </fieldset>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Code postal"
              placeholder="75011"
              error={errors.postal_code?.message}
              {...register("postal_code")}
            />
            <Field
              label="Ville"
              placeholder="Paris"
              error={errors.city?.message}
              {...register("city")}
            />
          </div>

          <Field
            label="Année de construction (estimation)"
            type="number"
            placeholder="1978"
            error={errors.construction_year?.message}
            {...register("construction_year")}
          />

          {serverError && (
            <p role="alert" className="text-sm text-red-700">
              {serverError}
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="rounded bg-blue-900 px-6 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
            >
              {createMutation.isPending ? "Création…" : "Créer le logement"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="rounded border border-gray-300 px-6 py-2 hover:bg-gray-100"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

type FieldProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  error?: string;
};

const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, error, id, ...rest },
  ref,
) {
  const inputId = id ?? rest.name;
  return (
    <div>
      <label htmlFor={inputId} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
        aria-invalid={!!error}
        aria-describedby={error ? `${inputId}-err` : undefined}
        {...rest}
      />
      {error && (
        <p id={`${inputId}-err`} role="alert" className="mt-1 text-xs text-red-700">
          {error}
        </p>
      )}
    </div>
  );
});

function TypeCard({
  value,
  label,
  current,
  register,
}: {
  value: "maison" | "appartement";
  label: string;
  current: string;
  register: ReturnType<typeof useForm<FormData>>["register"];
}) {
  const isSelected = current === value;
  return (
    <label
      className={`flex flex-1 cursor-pointer items-center justify-center rounded border-2 px-6 py-4 text-sm font-medium ${
        isSelected
          ? "border-blue-900 bg-blue-50 text-blue-900"
          : "border-gray-300 hover:border-blue-300"
      }`}
    >
      <input type="radio" value={value} className="sr-only" {...register("type")} />
      {label}
    </label>
  );
}
